import { FC, useEffect } from "react";
import { Controller } from "react-hook-form";
import { usePageContext } from "../../context/pageContext";
import { useI18n } from "../../hooks/useTranslations";
import { CostCenter } from "../../http/costCenter/types";
import Form from "../ui/Form";
import FormField from "../ui/FormField";
import Input from "../ui/Input";

export interface CostCenterFormBody {
  code: string;
  name: string;
}

type CostCenterFormProps = {
  costCenter?: CostCenter;
  onSubmit: (data: CostCenterFormBody) => void;
  isDisabled?: boolean;
  isLoading?: boolean;
};

const CostCenterForm: FC<CostCenterFormProps> = ({ costCenter, onSubmit, isDisabled, isLoading }) => {
  const i18n = useI18n();
  const pageContext = usePageContext();

  const defaultValues = {
    code: costCenter?.code ?? "",
    name: costCenter?.name ?? "",
  };

  useEffect(() => {
    pageContext.setPage({ title: i18n.t.costCenters.plural });
  }, [i18n.language]);

  return (
    <Form<CostCenterFormBody>
      defaultValues={defaultValues}
      submitText={i18n.t.common.save}
      disabled={isDisabled}
      onSubmit={() => onSubmit}
    >
      {({ control, reset }) => {
        useEffect(() => {
          reset(defaultValues);
        }, [costCenter]);

        return (
          <>
            <Controller
              name="code"
              control={control}
              rules={{ required: i18n.t.common.fieldCannotBeEmpty }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.t.common.code} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Input
                      id={labelId}
                      {...field}
                      isInvalid={isInvalid}
                      disabled={isDisabled || isLoading || !!costCenter}
                    />
                  )}
                </FormField>
              )}
            />

            <Controller
              name="name"
              control={control}
              rules={{ required: i18n.t.common.fieldCannotBeEmpty }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.t.common.name} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Input id={labelId} {...field} isInvalid={isInvalid} disabled={isDisabled || isLoading} />
                  )}
                </FormField>
              )}
            />
          </>
        );
      }}
    </Form>
  );
};

export default CostCenterForm;
